import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import styles from './Overlay.module.css';
import { CheckIcon } from '@/components/icons';

/**
 * Toasts (canvas 1m).
 *
 * The canvas draws the line between the three error surfaces: "Erro de operação é toast com
 * ação; erro de campo é inline; erro de tela é estado de página." So this is only for things
 * that already happened and can be retried or reviewed — never for validation.
 */

interface Toast {
  id: number;
  message: ReactNode;
  action?: { label: string; onClick: () => void };
  tone: 'success' | 'error';
}

interface ToastApi {
  success: (message: ReactNode, action?: Toast['action']) => void;
  error: (message: ReactNode, action?: Toast['action']) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

const DURATION_MS = 6000;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);

  const push = useCallback((tone: Toast['tone'], message: ReactNode, action?: Toast['action']) => {
    const id = nextId.current++;
    setToasts((list) => [...list, { id, message, action, tone }]);
    window.setTimeout(() => {
      setToasts((list) => list.filter((t) => t.id !== id));
    }, DURATION_MS);
  }, []);

  const api = useMemo<ToastApi>(
    () => ({
      success: (message, action) => push('success', message, action),
      error: (message, action) => push('error', message, action),
    }),
    [push],
  );

  const dismiss = useCallback((id: number) => {
    setToasts((list) => list.filter((t) => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className={styles.toastRegion} role="status" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={styles.toast}>
            {t.tone === 'success' && (
              <span className={styles.toastIcon} aria-hidden="true">
                <CheckIcon size={16} />
              </span>
            )}
            <span className={styles.toastText}>{t.message}</span>
            {t.action && (
              <button
                type="button"
                className={styles.toastAction}
                onClick={() => {
                  t.action?.onClick();
                  dismiss(t.id);
                }}
              >
                {t.action.label}
              </button>
            )}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast precisa estar dentro de <ToastProvider>');
  return ctx;
}
